import type { FC } from 'react';
import React, { createContext, memo, useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isUnauthorizedError } from '@confluence/unauthorized-error-boundary';
import {
	SiteInformationQuery,
	type SiteInformationQuery$data,
	type SiteInformationQuery$variables,
} from '@confluence/session-data/entry-points/SiteInformationQuery.graphql.ts';

export type SPAViewContextValue = {
	tenantId: string;
	isNewUser: boolean;
	userCanCreateContent: boolean;
	isAnonymous: boolean;
	siteLogoUrl: string;
	siteTitle: string;
	showSiteTitle: boolean;
	homepageUri: string;
	companyHubName: string;
	frontCoverState: string;
	homepageTitle: string;
	experimentFeatures: string;
	abTestCohorts: string;
	isSiteAdmin: boolean;
	loading?: boolean;
	error?: Error;
};

export const defaultContext: SPAViewContextValue = {
	tenantId: '',
	isNewUser: false,
	userCanCreateContent: false,
	isAnonymous: false,
	siteLogoUrl: '',
	siteTitle: '',
	showSiteTitle: false,
	homepageUri: '',
	homepageTitle: '',
	companyHubName: '',
	frontCoverState: '',
	experimentFeatures: '',
	abTestCohorts: '',
	isSiteAdmin: false,
	loading: true,
};

export const SPAViewContext = createContext<SPAViewContextValue>(defaultContext);
export const SPAViewContextProvider: FC<{
	children?: React.ReactNode;
}> = memo(({ children }) => {
	const { data, loading, error } = useQueryInterop<
		SiteInformationQuery$data,
		SiteInformationQuery$variables
	>(SiteInformationQuery);
	if (error) {
		if (!isUnauthorizedError(error)) {
			getMonitoringClient().submitError(error, { attribution: 'backbone' });
		}
		markErrorAsHandled(error);
	}

	// The context will fallback to default if any error happens
	const context = useMemo(
		() => ({
			...(data ? transformData(data) : defaultContext),
			loading,
			error,
		}),
		[loading, error, data],
	);

	return <SPAViewContext.Provider value={context}>{children}</SPAViewContext.Provider>;
});

type Nullable<T> = {
	[P in keyof T]: T[P] | null;
};

export const transformData = (data: SiteInformationQuery$data): SPAViewContextValue => {
	const { isSiteAdmin, abTestCohorts, experimentFeatures, userCanCreateContent, isNewUser } = data;
	const { uri: homepageUri, title: homepageTitle } = data?.siteSettings?.homepage || {};
	const { isAnonymous } = data?.currentConfluenceUser || {};
	const { logoUrl: siteLogoUrl } = data?.siteDescription || {};
	const { siteTitle } = data?.siteSettings || {};
	const { showApplicationTitle: showSiteTitle, companyHubName } = data?.siteSettings || {};
	const { frontCoverState } = data?.siteSettings?.frontCover || {};
	const tenantId = data?.tenant?.cloudId;
	const contextData: Nullable<Partial<SPAViewContextValue>> = {
		tenantId,
		isNewUser,
		userCanCreateContent,
		isAnonymous,
		siteLogoUrl,
		siteTitle,
		showSiteTitle,
		homepageUri,
		frontCoverState,
		companyHubName,
		homepageTitle,
		experimentFeatures,
		abTestCohorts,
		isSiteAdmin,
		loading: false,
	};

	// replace null values from query data with default values
	for (const [key, value] of Object.entries(contextData)) {
		if (value === null || value === undefined) {
			(contextData as any)[key] = (defaultContext as any)[key];
		}
	}

	return contextData as SPAViewContextValue;
};
